import { useCallback } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { useWarnOnFirstMobileInteraction, OpgavekompleksetComponent } from '@apps/opgavekomplekset';
import { getCaptionValue } from '@pm2/content-engine';
import { ElementToolbar } from '@pm2/element-toolbar';
import { scrollDirectionStore } from '@pm2/react-utils';
import { usePM2DisplayContext, pm2ConfigStore } from '@pm2/store';
import { usePm2Modal } from '@pm2/ui-lightbox';
import { componentFrameworkStore } from '@provider/component-framework';
import { useNavigationManager } from '@provider/navigation';
import { breakpoints } from '@ui/css';
import { useComponentEntry, UniversalLightboxCard, UniversalHarmonicaCard, UniversalFinishedCard } from '@utils/component-framework';
import { useMatchBreakpoint, useToggleState, SizeTransition } from '@utils/react';
import { EmbedContent } from './comp.embed-integration.js';
import { PM2OpgavekompleksetComponent } from './comp.root.js';
import { contentTransitionDurationMs, card, content } from './comp.router.style.js';
import { ScrambledCard } from './comp.scrambled.js';
import { jsx } from 'react/jsx-runtime';

function Router(props) {
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const navigation = useNavigationManager();
  const modal = usePm2Modal();
  const handleMobileWarningCallback = useWarnOnFirstMobileInteraction(props.component);
  const {
    inLightbox,
    inGrid
  } = usePM2DisplayContext();
  const resolvedDisplayMode = (() => {
    if (inGrid) {
      return "lightbox";
    }
    if (isMobile && props.displayMode === "harmonica") {
      return "lightbox";
    }
    return props.displayMode;
  })();
  const badgeImage = pm2ConfigStore.useSelector(state => state.config.theme.componentPreviewImage);
  const isEntryShared = useComponentEntry(props.component.entryKey, {
    select: entry => entry?.envelope.shared
  });
  const audience = pm2ConfigStore.useSelector(state => state.config.theme.targetAudience);
  const [expanded, expansionActions] = useToggleState();
  const state = (() => {
    if (props.isScrambled) {
      return "card.scrambled";
    }
    if (inLightbox) {
      return "lightbox";
    }
    if (!expanded) {
      switch (resolvedDisplayMode) {
        case "embed":
          return isEntryShared.data ? "card.finished" : "embed";
        case "harmonica":
          return "card.harmonica";
        case "lightbox":
          return "card.lightbox";
        default:
          console.warn(
          // @ts-expect-error: state should be of type "never" to guarantee
          // exhaustive cases above
          `libs.pm2/component.opgavekomplekset - <Router />: unexpected resolvedDisplayMode "${resolvedDisplayMode.toString()}"`);
      }
    }
    return "embed";
  })();
  const onShareWithTeacher = useCallback(() => {
    expansionActions.disable();
    setTimeout(() => {
      // Reset to the initial path upon closing the component
      navigation.push("/");
    }, contentTransitionDurationMs);
  }, [expansionActions, navigation]);
  const onLightboxActivate = useCallback(() => {
    componentFrameworkStore.dispatch(componentFrameworkStore.actions.activateComponent(props.node.component.envelope.ID));
  }, [props]);
  const onOpenLightbox = useCallback(() => {
    modal.fromComponentFrameworkNode(/*#__PURE__*/jsx(PM2OpgavekompleksetComponent, {
      ...props
    }), "component-framework.opgavekomplekset");

    // Wait until after the lightbox has been fully opened before potentially
    // triggering additional modals, in order to avoid z-index issues.
    setTimeout(() => {
      handleMobileWarningCallback();
    }, 150);
  }, [handleMobileWarningCallback, modal, props]);
  const onActivate = useCallback(() => {
    if (resolvedDisplayMode === "lightbox") {
      onOpenLightbox();
      return;
    }
    componentFrameworkStore.dispatch(componentFrameworkStore.actions.activateComponent(props.component.envelope.ID));
    expansionActions.enable();

    // Force the header to collapse upon opening the component
    scrollDirectionStore.dispatch(scrollDirectionStore.actions.setDirection("down"));
  }, [expansionActions, onOpenLightbox, props.component.envelope.ID, resolvedDisplayMode]);
  const onDeactivate = useCallback(() => {
    componentFrameworkStore.dispatch(componentFrameworkStore.actions.deactivateComponent(props.component.envelope.ID));
  }, [props.component.envelope.ID]);
  const onCollapse = useCallback(() => {
    expansionActions.disable();
    onDeactivate();
  }, [expansionActions, onDeactivate]);
  const content$1 = (() => {
    switch (state) {
      case "embed":
        return /*#__PURE__*/jsx(EmbedContent, {
          ...props,
          onActivate: onActivate,
          onDeactivate: onDeactivate,
          onShareWithTeacher: onShareWithTeacher,
          onOpenInLightbox: onOpenLightbox,
          onMountedInFullscreen: onLightboxActivate,
          onCollapse: resolvedDisplayMode === "harmonica" || isEntryShared.data && resolvedDisplayMode === "embed" ? onCollapse : undefined
        });
      case "lightbox":
        return /*#__PURE__*/jsx(OpgavekompleksetComponent, {
          component: props.component,
          onResetEntry: props.onResetEntry,
          context: "lightbox"
        });
      case "card.finished":
        return /*#__PURE__*/jsx(UniversalFinishedCard, {
          className: card,
          title: props.component.envelope.Title,
          onClick: onActivate,
          children: /*#__PURE__*/jsx(UniversalFinishedCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "card.harmonica":
        return /*#__PURE__*/jsx(UniversalHarmonicaCard, {
          className: card,
          title: getCaptionValue(props.node.meta.caption.title),
          description: props.richtextDescription,
          audience: audience,
          hasShared: isEntryShared.data,
          badgeImageSrc: badgeImage,
          indexOrLevel: props.indexOrLevel,
          onClick: onActivate,
          children: /*#__PURE__*/jsx(UniversalHarmonicaCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "card.lightbox":
        return /*#__PURE__*/jsx(UniversalLightboxCard, {
          className: card,
          title: getCaptionValue(props.node.meta.caption.title),
          description: props.lightboxDescription,
          audience: audience,
          hasShared: isEntryShared.data,
          badgeImageSrc: badgeImage,
          previewImageSrc: props.previewImage,
          indexOrLevel: props.indexOrLevel,
          onClick: onOpenLightbox,
          children: /*#__PURE__*/jsx(UniversalLightboxCard.ContextMenu, {
            children: /*#__PURE__*/jsx(ElementToolbar, {
              node: props.node
            })
          })
        });
      case "card.scrambled":
        return /*#__PURE__*/jsx(ScrambledCard, {
          className: card,
          title: getCaptionValue(props.node.meta.caption.title) ?? ""
        });
      default:
        console.warn(
        // @ts-expect-error: state should be of type "never" to guarantee
        // exhaustive cases above
        `libs.pm2/component.i-guide - <Router />: unexpected state "${state.toString()}"`);
    }
  })();
  return /*#__PURE__*/jsx(TransitionGroup, {
    component: null,
    children: /*#__PURE__*/jsx(SizeTransition, {
      axis: "vertical",
      swappable: true,
      classNames: content,
      timeout: contentTransitionDurationMs,
      delayMs: {
        enter: 10,
        exit: 0
      },
      onEnter: () => document.documentElement.style.setProperty("overflow-anchor", "none"),
      onEntered: () => document.documentElement.style.setProperty("overflow-anchor", ""),
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        "data-node-id": props.component.envelope.ID,
        className: content,
        children: content$1
      })
    }, state)
  });
}

export { Router };
//# sourceMappingURL=comp.router.js.map
